/**
 * Component Usage Prompt
 *
 * Helps generate correct component usage with proper props.
 */

import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import { z } from 'zod';
import componentCatalog from '../data/component-catalog.json' with { type: 'json' };
import type { ComponentCatalog } from '../types.js';

const catalog = componentCatalog as ComponentCatalog;

const ComponentUsageSchema = z.object({
  componentName: z.string().describe('Name of the component (e.g., Button, Card, Select)'),
  variant: z.string().optional().describe('Desired variant (optional)'),
  useCase: z.string().optional().describe('Brief description of use case'),
});

/**
 * Register the component_usage prompt
 */
export function registerComponentUsagePrompt(server: McpServer): void {
  server.prompt(
    'component_usage',
    'Generate correct usage for a Classic Theme component',
    ComponentUsageSchema.shape,
    async (params) => {
      const parsed = ComponentUsageSchema.parse(params);
      const componentName = parsed.componentName || 'Button';
      const variant = parsed.variant || '';
      const useCase = parsed.useCase || '';

      const component = catalog.components.find(
        (c) => c.name.toLowerCase() === componentName.toLowerCase()
      );

      let componentInfo = '';
      if (component) {
        componentInfo = `
Component: ${component.name}
Description: ${component.description}
Category: ${component.category}

Props:
${component.props.map((p) => `- ${p.name}: ${p.type} ${p.required ? '(required)' : `(default: ${p.default || 'undefined'})`} - ${p.description}`).join('\n')}

Variants:
${component.variants.map((v) => `- ${v.name}: ${v.values.join(' | ')} (default: ${v.default || v.values[0]})`).join('\n')}

Examples:
${component.examples.map((e) => `### ${e.title}\n\`\`\`svelte\n${e.code}\n\`\`\``).join('\n\n')}`;
      } else {
        componentInfo = `Component "${componentName}" not found in catalog. Available components: ${catalog.components.map((c) => c.name).join(', ')}`;
      }

      return {
        messages: [
          {
            role: 'user',
            content: {
              type: 'text',
              text: `Generate correct usage for the ${componentName} component from @classic-homes/theme-svelte.
${variant ? `Desired variant: ${variant}` : ''}
${useCase ? `Use case: ${useCase}` : ''}

${componentInfo}

Requirements:
- Import from '@classic-homes/theme-svelte'
- Use Svelte 5 runes syntax ($props, $state, etc.)
- Include proper TypeScript types
- Follow accessibility best practices
- Add appropriate event handlers if needed`,
            },
          },
        ],
      };
    }
  );
}
